import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AccessiblePrice } from '@/components/AccessiblePrice'
import type { ProductOfferFragment } from '@/services/graphql/generated'
import { StoryblokComponent } from '@storyblok/react'
import { Card } from 'ui/src/components/Card/Card'
import { Dialog } from 'ui/src/components/Dialog/Dialog'
import { LargeDialog } from 'ui/src/components/Dialog/LargeDialog'
import { Text } from 'ui/src/components/Text/Text'
import { yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { usePriceCalculatorInsuranceAmountInfo } from '../priceCalculatorAtoms'
import { CardRadioGroup } from './CardRadioGroup/CardRadioGroup'
import { InfoButton } from './InfoButton/InfoButton'
import { textTransition, vars } from './CardRadioGroup/CardRadioGroup.css'

export type ProductOfferWithInsuranceAmount = ProductOfferFragment & {
  insuranceAmount: NonNullable<ProductOfferFragment['insuranceAmount']>
}

type Props = {
  offers: Array<ProductOfferWithInsuranceAmount>
  selectedOfferId: string
  onValueChange: (offerId: string) => void
}

export function InsuranceAmountSelector({ offers, selectedOfferId, onValueChange }: Props) {
  const insuranceAmountLevels = useMemo(
    () =>
      offers.map((offer) => {
        return {
          id: offer.id,
          price: offer.cost.baseInsuranceGross,
          title: offer.insuranceAmount.displayName,
        }
      }),
    [offers],
  )

  return (
    <div className={yStack({ alignItems: 'stretch', gap: 'md' })}>
      <CardRadioGroup.Root value={selectedOfferId} onValueChange={onValueChange}>
        {insuranceAmountLevels.map((item) => {
          const labelledById = `${item.id}-insurance-amount-title`
          const describedById = `${item.id}-insurance-amount-price`

          return (
            <CardRadioGroup.Item
              key={item.id}
              value={item.id}
              aria-labelledby={labelledById}
              aria-describedby={describedById}
            >
              <Card.Aside floating>
                <CardRadioGroup.Badge isSelected={selectedOfferId === item.id}>
                  <span id={describedById}>
                    <AccessiblePrice price={item.price} />
                  </span>
                </CardRadioGroup.Badge>
              </Card.Aside>
              <Card.Header>
                <Card.Heading>
                  <Card.Title
                    id={labelledById}
                    as="h2"
                    className={textTransition}
                    variant="standard.24"
                    style={{ color: vars.titleColor }}
                  >
                    {item.title}
                  </Card.Title>
                </Card.Heading>
              </Card.Header>
            </CardRadioGroup.Item>
          )
        })}
      </CardRadioGroup.Root>

      <InsuranceAmountInfo />
    </div>
  )
}

function InsuranceAmountInfo() {
  const { t } = useTranslation(['common', 'purchase-form'])
  const insuranceAmountInfo = usePriceCalculatorInsuranceAmountInfo()

  if (!insuranceAmountInfo) return null

  return (
    <LargeDialog.Root>
      <Dialog.Trigger asChild={true}>
        <InfoButton>
          <div className={yStack()}>
            <Text as="div" size="xs">
              {t('purchase-form:INSURANCE_AMOUNT_SELECTOR_DIALOG_TRIGGER')}
            </Text>
            <Text as="span" color="textSecondary" size="xs" balance={true}>
              {t('purchase-form:INSURANCE_AMOUNT_SELECTOR_DIALOG_DESCRIPTION')}
            </Text>
          </div>
        </InfoButton>
      </Dialog.Trigger>
      <LargeDialog.Content className={sprinkles({ paddingBlock: 0 })}>
        <LargeDialog.CloseIconButton aria-label={t('common:DIALOG_CLOSE')} />
        <Dialog.Title className={sprinkles({ display: 'none' })}>
          {t('purchase-form:INSURANCE_AMOUNT_SELECTOR_DIALOG_TITLE')}
        </Dialog.Title>
        {insuranceAmountInfo?.map((blok) => (
          <StoryblokComponent key={blok._uid} blok={blok} nested={true} />
        ))}
      </LargeDialog.Content>
    </LargeDialog.Root>
  )
}
